import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  fetchWorkOrderManufacturingCommandStatus,
  fetchWorkOrderMaterialShortage,
} from '#/api/momMgmt/workOrder';
import { coerceFilterValues } from '#/modules/commonComponents/table/columnFilterUtils';
import {
  matchesTextFilter,
} from '#/modules/commonComponents/table/clientListFilterMatchers';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { mergeFilterUpdate } from '#/modules/commonComponents/table/listFilterKeys';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { SelectCore } from '#/modules/commonComponents/form/select';
import AffectedWorkOrdersDialog from '#/modules/momMgmt/analysis/materials/components/AffectedWorkOrdersDialog';
import { buildMaterialShortageColumns } from '#/modules/momMgmt/analysis/materials/data/materialShortageColumns';
import { fetchWarehouseSelectOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import { getWorkOrderApiErrorMessage } from '#/modules/momMgmt/workOrder/helper/apiError';
import {
  lookupQueryKey,
  workOrderManufacturingCommandStatusQueryKey,
  workOrderMaterialShortageQueryKey,
} from '#/utils/queryKeys';

const EMPTY_LIST_QUERY = {
  keyword: '',
  sort: [],
  filters: {},
};

const ALL_WAREHOUSE_OPTION = { value: '', label: '全部' };

const SORT_FIELD_LABELS = {
  mtrNo: '料號',
  mtrName: '名稱',
  requiredQty: '需求',
  issuedQty: '已出庫',
  onHandAllocatedQty: '庫存',
  inTransitAllocatedQty: '在途',
  unknownArrivalQty: '未排到貨',
  shortageQty: '缺口',
  affectedWorkOrderNos: '影響工單',
};

function matchesKeyword(row, keyword) {
  if (!keyword) {
    return true;
  }
  const q = keyword.trim().toLowerCase();
  if (!q) {
    return true;
  }
  const haystack = [
    row?.mtr?.mtrNo,
    row?.mtr?.name,
    ...(Array.isArray(row?.affectedWorkOrderNos) ? row.affectedWorkOrderNos : []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

function matchesClientFilters(row, listQuery) {
  const filters = listQuery.filters || {};

  return (
    matchesKeyword(row, listQuery.keyword)
    && matchesTextFilter(row, 'mtr.mtrNo', filters.mtrNo)
    && matchesTextFilter(row, 'mtr.name', filters.mtrName)
    && matchesShortageOnlyFilter(row, filters)
  );
}

function matchesShortageOnlyFilter(row, filters) {
  const selected = coerceFilterValues(filters?.shortageOnly);
  if (!selected.includes('true')) {
    return true;
  }
  const shortage = Number(row?.shortageQty);
  return Number.isFinite(shortage) && shortage > 0;
}

function resolveRowId(row, index) {
  if (row?.mtr?.id != null && row.mtr.id !== '') {
    return String(row.mtr.id);
  }
  if (row?.mtr?.mtrNo) {
    return String(row.mtr.mtrNo);
  }
  return `shortage-${index}`;
}

const MaterialShortageListPage = () => {
  const [listQuery, setListQuery] = useState(EMPTY_LIST_QUERY);
  const [affectedRow, setAffectedRow] = useState(null);
  const [workWarehouseId, setWorkWarehouseId] = useState('');

  const handleViewAffected = useCallback((row) => {
    setAffectedRow(row);
  }, []);

  const handleFilterChange = useCallback((key, value) => {
    setListQuery((prev) => ({
      ...prev,
      filters: mergeFilterUpdate(prev.filters, key, value),
    }));
  }, []);

  const warehouseOptionsQuery = useQuery({
    queryKey: lookupQueryKey('warehouseSelect'),
    queryFn: fetchWarehouseSelectOptions,
  });

  const warehouseSelectOptions = useMemo(
    () => [
      ALL_WAREHOUSE_OPTION,
      ...(warehouseOptionsQuery.data ?? []),
    ],
    [warehouseOptionsQuery.data],
  );

  const workWarehouseIds = useMemo(
    () => (workWarehouseId ? [workWarehouseId] : []),
    [workWarehouseId],
  );

  const columns = useMemo(
    () => buildMaterialShortageColumns({
      filters: listQuery.filters || {},
      onFilterChange: handleFilterChange,
      onViewAffected: handleViewAffected,
    }),
    [listQuery.filters, handleFilterChange, handleViewAffected],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const shortageQuery = useQuery({
    queryKey: workOrderMaterialShortageQueryKey(workWarehouseIds),
    queryFn: () => fetchWorkOrderMaterialShortage({ workWarehouseIds }),
  });

  const statusQuery = useQuery({
    queryKey: workOrderManufacturingCommandStatusQueryKey(),
    queryFn: () => fetchWorkOrderManufacturingCommandStatus(),
  });

  const workOrderNoToId = useMemo(() => {
    const map = new Map();
    const workOrders = statusQuery.data?.data?.workOrders ?? [];
    workOrders.forEach((wo) => {
      if (wo?.workOrderNo == null || wo?.id == null || wo.id === '') {
        return;
      }
      map.set(String(wo.workOrderNo), String(wo.id));
    });
    return map;
  }, [statusQuery.data?.data?.workOrders]);

  const rows = useMemo(() => {
    const list = shortageQuery.data?.data ?? [];
    return list
      .map((row, index) => ({
        ...row,
        id: resolveRowId(row, index),
      }))
      .filter((row) => matchesClientFilters(row, listQuery));
  }, [shortageQuery.data?.data, listQuery]);

  function handleSearch(keyword) {
    setListQuery((prev) => ({ ...prev, keyword: keyword ?? '' }));
  }

  function handleReset() {
    setWorkWarehouseId('');
    setListQuery(EMPTY_LIST_QUERY);
  }

  function handleSortingChange(sorting) {
    setListQuery((prev) => ({
      ...prev,
      sort: (sorting ?? []).map((item) => ({
        id: item.id,
        desc: Boolean(item.desc),
      })),
    }));
  }

  function handleSortRemove(index) {
    setListQuery((prev) => ({
      ...prev,
      sort: (prev.sort ?? []).filter((_, i) => i !== index),
    }));
  }

  const toolbarAfterSearch = (
    <SelectCore
      id="materialShortageWorkWarehouse"
      label="工作倉"
      options={warehouseSelectOptions}
      value={workWarehouseId}
      onChange={(value) => setWorkWarehouseId(value ?? '')}
      isDisabled={warehouseOptionsQuery.isLoading}
      placeholder="工作倉"
      hideErrorMessage
      containerClassName="mb-0! w-44 shrink-0"
      noOptionsMessage="無可用倉別"
    />
  );

  return (
    <>
      <div className="table-panel min-h-0 flex-1">
        <ListTablePanel
          columns={columns}
          rows={rows}
          isLoading={shortageQuery.isLoading}
          isError={shortageQuery.isError}
          errorMessage={
            shortageQuery.isError
              ? getWorkOrderApiErrorMessage(shortageQuery.error, '載入列表失敗')
              : undefined
          }
          onRetry={shortageQuery.isError
            ? () => { shortageQuery.refetch(); }
            : undefined}
          serverSide={false}
          listQuery={listQuery}
          filterStatusFields={filterStatusFields}
          sortFieldLabels={SORT_FIELD_LABELS}
          toolbarAfterSearch={toolbarAfterSearch}
          onSearch={handleSearch}
          onReset={handleReset}
          initialKeyword={listQuery.keyword}
          searchPlaceholder="搜尋料號、名稱或工單號"
          onKeywordClear={() => handleSearch('')}
          onFilterChange={handleFilterChange}
          onSortingChange={handleSortingChange}
          onSortRemove={handleSortRemove}
        />
      </div>

      <AffectedWorkOrdersDialog
        open={Boolean(affectedRow)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setAffectedRow(null);
          }
        }}
        mtr={affectedRow?.mtr}
        workOrderNos={affectedRow?.affectedWorkOrderNos ?? []}
        workOrderNoToId={workOrderNoToId}
      />
    </>
  );
};

export default MaterialShortageListPage;
